import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wand2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const Hero = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const wavePointsRef = useRef<Array<{
    x: number;
    y: number;
    amplitude: number;
    frequency: number;
    phase: number;
  }>>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      
      // Initialize wave points
      wavePointsRef.current = [];
      const numWaves = 3;
      for (let i = 0; i < numWaves; i++) {
        wavePointsRef.current.push({
          x: 0,
          y: canvas.height / 2 + i * 30,
          amplitude: 40 + i * 20,
          frequency: 0.002 + i * 0.001,
          phase: i * Math.PI / 3
        });
      }
    };

    const drawWave = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      wavePointsRef.current.forEach((wave, index) => {
        ctx.beginPath();
        // Mix purple with bright white for better visibility
        const opacity = 0.6 - index * 0.15;
        const whiteOpacity = 0.3 - index * 0.08;
        ctx.strokeStyle = `rgba(255, 255, 255, ${whiteOpacity})`;
        ctx.shadowColor = `rgba(138, 43, 226, ${opacity})`;
        ctx.shadowBlur = 3;
        ctx.lineWidth = 2 + index * 0.5;
        
        for (let x = 0; x < canvas.width; x += 2) {
          const y = wave.y + Math.sin((x * wave.frequency) + (time * 0.001) + wave.phase) * wave.amplitude;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
        
        // Add a second stroke with purple for the color mix effect
        ctx.beginPath();
        ctx.strokeStyle = `rgba(168, 85, 247, ${opacity * 0.7})`;
        ctx.shadowColor = 'transparent';
        ctx.lineWidth = 1;
        
        for (let x = 0; x < canvas.width; x += 4) {
          const y = wave.y + Math.sin((x * wave.frequency) + (time * 0.001) + wave.phase + 0.5) * wave.amplitude * 0.8;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      });
      
      animationRef.current = requestAnimationFrame(drawWave);
    };

    // Set up resize observer
    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(canvas);
    resizeCanvas();
    
    // Start animation
    animationRef.current = requestAnimationFrame(drawWave);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const handleTryFree = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const scrollToVoiceModels = () => {
    const voiceModelsSection = document.getElementById('voice-models');
    if (voiceModelsSection) {
      voiceModelsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-purple-900 via-purple-950 to-[#1a0635] flex items-center justify-center">
      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))] pointer-events-none"></div>
      
      {/* Animated floating particles background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-float-slow"></div>
      </div>
      
      {/* Main Content - Properly Centered */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex flex-col items-center justify-center text-center space-y-8">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-center max-w-5xl leading-tight">
            <span className="text-white">Best Text to Speech</span>{' '}
            <span 
              className="bg-gradient-to-r from-purple-400 via-pink-400 via-cyan-400 via-purple-400 via-yellow-400 via-pink-400 to-purple-400 bg-clip-text text-transparent"
              style={{
                backgroundSize: '400% 400%',
                animation: 'gradientWave 4s ease-in-out infinite'
              }}
            >
              AI Voice Generator
            </span>{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Free Online
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 text-center max-w-3xl leading-relaxed">
            The best text to speech software in 2026. Convert text to natural-sounding speech 
            instantly with 500+ AI voices across 50+ languages. Perfect for content creators, 
            educators, and businesses. Try our free AI voice generator today!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <button
              onClick={handleTryFree}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-lg rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-purple-500/25"
            >
              <Wand2 className="w-5 h-5 mr-2" />
              Try for Free
            </button>
            <button 
              onClick={scrollToVoiceModels}
              className="inline-flex items-center px-8 py-4 border-2 border-gray-400 text-gray-300 font-semibold text-lg rounded-xl hover:border-white hover:text-white transition-all duration-200"
            >
              View Demo
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 mt-16">
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">50M+</p>
              <p className="text-sm text-gray-400 mt-1">Characters Generated</p>
            </div>
            <div className="hidden sm:block w-px h-8 bg-gray-600"></div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">500+</p>
              <p className="text-sm text-gray-400 mt-1">AI Voices</p>
            </div>
            <div className="hidden sm:block w-px h-8 bg-gray-600"></div>
            <div className="text-center">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-pink-400 to-blue-400 bg-clip-text text-transparent">4.9/5</p>
              <p className="text-sm text-gray-400 mt-1">User Rating</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Animated wave canvas at the bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-48">
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        />
      </div>
      
      {/* CSS for gradient wave animation */}
      <style>{`
        @keyframes gradientWave {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;